"use strict";
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getUniformLocation = exports.createBuffer = exports.createProgram = void 0;
/**
 * WebGL2 helpers shared by the custom layers. They are called when a layer creates its GPU
 * resources, not at every frame. Each one throws a descriptive error instead of returning null,
 * so a failure is reported where it happens.
 */
/** Prefix each line of a shader source with its number, to match the line numbers of the GLSL compiler log */
const numberLines = (source) => source
    .split('\n')
    .map((line, i) => `${String(i + 1).padStart(4)}: ${line}`)
    .join('\n');
/**
 * Compile one shader, the GLSL program run by the GPU for each vertex or each pixel. Compilation
 * errors throw with the GLSL log and the numbered source.
 *
 * @param gl The WebGL2 context
 * @param type `gl.VERTEX_SHADER` or `gl.FRAGMENT_SHADER`
 * @param source GLSL source of the shader
 * @returns The compiled shader, to attach to a program
 */
const compileShader = (gl, type, source) => {
    const shader = gl.createShader(type);
    if (!shader) {
        throw new Error('Could not create WebGL shader');
    }
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        const log = gl.getShaderInfoLog(shader);
        gl.deleteShader(shader);
        const stage = type === gl.VERTEX_SHADER ? 'vertex' : 'fragment';
        throw new Error(`Could not compile ${stage} shader: ${log}\n${numberLines(source)}`);
    }
    return shader;
};
/**
 * Compile and link a WebGL2 program. Compilation and link errors throw with the GLSL log,
 * and the numbered shader source for compilation errors.
 *
 * @param gl The WebGL2 context
 * @param vertexSource GLSL source of the vertex shader
 * @param fragmentSource GLSL source of the fragment shader
 * @returns The linked program. The shaders are detached and deleted.
 */
const createProgram = (gl, vertexSource, fragmentSource) => {
    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
    let fragmentShader;
    try {
        fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
    }
    catch (error) {
        gl.deleteShader(vertexShader);
        throw error;
    }
    const program = gl.createProgram();
    if (!program) {
        gl.deleteShader(vertexShader);
        gl.deleteShader(fragmentShader);
        throw new Error('Could not create WebGL program');
    }
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.detachShader(program, vertexShader);
    gl.detachShader(program, fragmentShader);
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        const log = gl.getProgramInfoLog(program);
        gl.deleteProgram(program);
        throw new Error(`Could not link WebGL program: ${log}`);
    }
    return program;
};
exports.createProgram = createProgram;
/**
 * Create a WebGL buffer, throwing if the context cannot create it (e.g. context lost).
 * @param gl The WebGL2 context
 * @returns The new buffer
 */
const createBuffer = (gl) => {
    const buffer = gl.createBuffer();
    if (!buffer) {
        throw new Error('Could not create WebGL buffer');
    }
    return buffer;
};
exports.createBuffer = createBuffer;
/**
 * Get the location of a uniform, throwing if the program does not use it,
 * so a typo or a uniform optimized away by the compiler is caught right away.
 *
 * @param gl The WebGL2 context
 * @param program The linked program
 * @param name The uniform name in the GLSL source
 * @returns The uniform location
 */
const getUniformLocation = (gl, program, name) => {
    const location = gl.getUniformLocation(program, name);
    if (location === null) {
        throw new Error(`Uniform ${name} not found in the WebGL program`);
    }
    return location;
};
exports.getUniformLocation = getUniformLocation;
//# sourceMappingURL=glUtils.js.map